import { Field } from 'formik'
import styles from './CheckoutForm.module.css'
import cx from 'classnames'
import { CreditCard } from '../CreditCard/CreditCard'
import { Button, ButtonType, ButtonVariant } from '@local/do-secundo-button'
import React, { useEffect } from 'react'
import { CheckoutFormBodyProps } from './utils'
import { CreditCardFormSpi } from '../CreditCard/CreditCardFormSpi'
import { getCheckTotal } from '../../utils/cart-helpers'
import { CheckoutDetailsStep } from './CheckoutDetailsStep'
import { LegalCopy } from '../LegalCopy/LegalCopy'
import { CheckoutMode, useCustomerAuth } from '../../auth/CustomerAuthContext'
import { CartCheckoutFields } from './CartCheckoutFields'
import { SelectCheckoutMode } from '../CheckoutPage/SelectCheckoutMode'
import { Loading, LoadingVariant } from '@local/do-secundo-loading'

/**
 * Handles single-step checkout for domestic restaurants with inline payment collection.
 * Renders credit card form and "Place order" button, delegating form layout to CheckoutDetailsStep.
 * Selected by CheckoutFormContent when isIntlRx is false.
 */
export const DomesticCheckoutFormBody = (props: CheckoutFormBodyProps) => {
  const {
    customer,
    values,
    isSubmitting,
    canCheckout,
    confirmOrderError,
    setIsAuthModalOpen,
    isAuthModalOpen,
    checkoutProps,
    showCreditCard,
    tipAmount
  } = props
  const { spiEnabled, restaurantInfo, cart } = checkoutProps
  const {
    loadingCustomer,
    checkoutMode,
    hasSelectedCheckoutMode,
    setHasSelectedCheckoutMode
  } = useCustomerAuth()

  useEffect(() => {
    if (!loadingCustomer && checkoutMode === CheckoutMode.Toast) {
      setHasSelectedCheckoutMode(true)
    }
  }, [loadingCustomer, checkoutMode, setHasSelectedCheckoutMode])

  // Show loading while checking customer auth
  if (loadingCustomer && !isAuthModalOpen) {
    return <Loading variant={LoadingVariant.SECONDARY} />
  }

  const showLoginPrompt =
    !hasSelectedCheckoutMode && checkoutMode !== CheckoutMode.Toast

  if (showLoginPrompt) {
    return (
      <>
        <CartCheckoutFields
          values={values}
          customer={customer}
          cart={cart!}
          showTip={'hide'}
        />
        <SelectCheckoutMode setIsAuthModalOpen={setIsAuthModalOpen} />
      </>
    )
  }

  const creditCardSection = showCreditCard && (
    <div
      className={cx({
        // Only show CC if paymentType is not OTHER aka Pay Later
        // Use CSS to hide it rather than conditionally rendering since this is an iframe
        // and we don't want to have to reload it
        [styles.hidden]: values.paymentType === 'OTHER'
      })}
    >
      {spiEnabled ? (
        <Field
          component={CreditCardFormSpi}
          name='isPaymentInfoValid'
          cartGuid={cart!.guid}
          amount={getCheckTotal(cart!)}
          tipAmount={tipAmount}
          email={customer.email}
          errorAuthorizingPayment={
            confirmOrderError?.errorType === 'PAYMENT_AUTH'
              ? confirmOrderError.error
              : undefined
          }
        />
      ) : (
        <Field
          component={CreditCard}
          name='encryptedCard'
          amexAccepted={restaurantInfo?.amexAccepted}
        />
      )}
    </div>
  )

  return (
    <>
      <CheckoutDetailsStep {...props} creditCardSection={creditCardSection} />
      <div className={styles.wrapper}>
        <Button
          data-testid='submit-button'
          id='submit-button'
          variant={ButtonVariant.PRIMARY}
          loading={isSubmitting}
          disabled={!canCheckout}
          type={ButtonType.SUBMIT}
        >
          Place order
        </Button>
        <LegalCopy />
        <div className='h-6' />
      </div>
    </>
  )
}
